import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { usePerformToggle } from "../../../hooks/appContext"
import { useDebounce } from "../../../hooks/useDebounce"
import { useGetProfileQuery } from "../../../store/messagingSlice"
import { addPageAction } from "../../../utils/newrelic"
import { ChevronLeftBlue, Fanclub } from "../../common/atoms/Icons/Chat"
import { IconButton } from "../common/IconButton/IconButton"
import { ProfilePicture } from "../common/ProfilePicture/ProfilePicture"
import { MessageFollowStar as FollowStar } from "../FollowStar/FollowStar"
import { useMessagingView } from "../hooks"
import { useFindThreadInCaches } from "../hooks/useFindThreadInCaches"
import { useVisualViewport } from "../hooks/useVisualViewport"
import { MessageHeaderMenu } from "../MessageHeaderMenu/MessageHeaderMenu"
import { UserMenu } from "../UserMenu"
import { classNames, resolveUserColorClass } from "../utils"
import styles from "./MessageHeader.module.scss"
import type { IUserInfo } from "@multimediallc/web-utils/types"

interface MessageHeaderProps {
    username: string
    onClose: () => void
    isMinimized: boolean
    onMinimize: () => void
    numUnread?: number
    inFanclub?: boolean
    onReportClick: () => void
    onNoteClick: () => void
    isReportEnabled: boolean
    messageInputHasFocus: boolean
    trackNewConversationExit: () => void
}

export function MessageHeader({
    username,
    onClose,
    isMinimized,
    onMinimize,
    numUnread = 0,
    onReportClick,
    onNoteClick,
    isReportEnabled,
    messageInputHasFocus,
    trackNewConversationExit,
}: MessageHeaderProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const messagingView = useMessagingView()
    const { vvOffsetTop } = useVisualViewport()
    const [showFanclubBadge, setShowFanclubBadge] = useState<boolean>(false)
    const [animatedTop, setAnimatedTop] = useState<number>(0)
    const debouncedMessageInputHasFocus = useDebounce(messageInputHasFocus, 10) // debounce to avoid flicker on ios
    const isMsgFutureRlsActive = usePerformToggle("MsgFutureRls").active

    const targetThread = useFindThreadInCaches(username)
    const otherUserFromThreadCache = targetThread?.other_user
    const { data: profile } = useGetProfileQuery(username || "")

    const user: IUserInfo = profile?.user ||
        otherUserFromThreadCache || {
            username,
            isBroadcaster: false,
            inFanclub: false,
            isFollowing: false,
            hasTokens: false,
            isMod: false,
            tippedRecently: false,
            tippedALotRecently: false,
            tippedTonsRecently: false,
            gender: "",
        }

    useEffect(() => {
        const newShowFanclubBadgeValue = isMsgFutureRlsActive && user?.inFanclub
        setShowFanclubBadge(newShowFanclubBadgeValue)
    }, [user])

    const handleBackClick = () => {
        addPageAction("DmCloseConversation")
        trackNewConversationExit()
        const hasHistory = location.key !== "default"
        if (hasHistory) {
            navigate(-1)
            return
        }
        navigate("/", { replace: true })
    }

    const userColorClass = resolveUserColorClass(user)

    useEffect(() => {
        setAnimatedTop(-100)
        requestAnimationFrame(() => {
            setAnimatedTop(vvOffsetTop)
        })
    }, [vvOffsetTop])

    const UserInfo = () => (
        <div className={styles.userInfoContainer}>
            <ProfilePicture
                user={user}
                size={32}
                className={styles.profilePicture}
            />
            <div
                className={classNames(styles.username, {
                    [styles.mobile]: messagingView.isMobile,
                })}
            >
                <span
                    className={classNames(styles.usernameText, userColorClass)}
                >
                    {username}
                </span>
                {showFanclubBadge && (
                    <span
                        className={styles.fanclubIcon}
                        data-testid="fanclub-icon"
                    >
                        <Fanclub width={16} height={16} />
                    </span>
                )}
            </div>
        </div>
    )

    return (
        <div
            className={classNames(styles.header, {
                [styles.mobile]: messagingView.isMobile,
                [styles.desktop]: messagingView.isDesktopConversation,
                [styles.minimized]: isMinimized,
            })}
            style={{
                top: debouncedMessageInputHasFocus ? animatedTop : 0,
                transition: debouncedMessageInputHasFocus
                    ? "top 0.2s ease"
                    : "none",
            }}
            onClick={isMinimized ? onMinimize : undefined}
        >
            {messagingView.isMobile && !isMinimized && (
                <IconButton
                    className={styles.backButton}
                    icon={<ChevronLeftBlue height={16} width={10} />}
                    iconAlt="Back"
                    onClick={handleBackClick}
                />
            )}

            {!isMinimized ? (
                <UserMenu username={username} toggle={<UserInfo />} />
            ) : (
                <UserInfo />
            )}

            <div className={styles.followStarWrapper}>
                <FollowStar username={username} />
            </div>
            <MessageHeaderMenu
                onClose={onClose}
                onMinimize={onMinimize}
                isMinimized={isMinimized}
                onConversationDeleted={handleBackClick}
                onReportClick={onReportClick}
                onNoteClick={onNoteClick}
                isReportEnabled={isReportEnabled}
                numUnread={numUnread}
            />
        </div>
    )
}
